import React from 'react';
import { useNavigate, Outlet } from '@tanstack/react-router';
import { ApiError } from '@daater/api';
import { useAuthStore } from '../lib/auth-store';
import { useAccountStore } from '../lib/account-store';
import { useCurrentAccountQuery } from '../api/queries';
import {
  buildPaymentRedirectSearch,
  classifyCurrentAccountAccess,
} from '../api/current-account-access';
import AppShell from './app-shell';

export default function AuthLayout() {
  const user = useAuthStore(state => state.user);
  const token = useAuthStore(state => state.token);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const navigate = useNavigate();

  const { data, error, isFetchedAfterMount, isLoading } = useCurrentAccountQuery(token);
  const access = React.useMemo(
    () => classifyCurrentAccountAccess({ account: data, error }),
    [data, error]
  );
  const isPaymentRoute = window.location.pathname === '/payment';
  const currentSearchString = window.location.search;
  const paymentSearch = React.useMemo(
    () => buildPaymentRedirectSearch(currentSearchString, access.amount),
    [access.amount, currentSearchString]
  );
  const shouldRedirectToPayment = React.useMemo(() => {
    if (access.decision !== 'payment') return false;
    return !isPaymentRoute;
  }, [access.decision, isPaymentRoute]);

  React.useEffect(() => {
    if (!token || !user) {
      void navigate({ to: '/login', replace: true });
    }
  }, [navigate, token, user]);

  React.useEffect(() => {
    if (!token || !user || isLoading || !isFetchedAfterMount) return;

    if (access.decision === 'login') {
      if (error instanceof ApiError && error.status === 401) {
        clearAuth();
        clearAccount();
      }

      void navigate({ to: '/login', replace: true });
      return;
    }

    if (shouldRedirectToPayment) {
      void navigate({
        to: '/payment',
        replace: true,
        search: paymentSearch,
      });
    }
  }, [
    access.decision,
    clearAccount,
    clearAuth,
    error,
    isFetchedAfterMount,
    isLoading,
    navigate,
    paymentSearch,
    shouldRedirectToPayment,
    token,
    user,
  ]);

  if (!token || !user) return null;

  if (isLoading || !isFetchedAfterMount) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pale-grey">
        <div className="rounded-lg border border-pale-grey-three bg-white px-6 py-4 text-sm text-slate">
          Cargando cuenta...
        </div>
      </div>
    );
  }

  if (access.decision === 'login') return null;

  if (shouldRedirectToPayment) return null;

  if (access.decision === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pale-grey">
        <div className="max-w-md rounded-xl bg-white p-6 text-center shadow-[0_4px_20px_-4px_rgba(26,46,88,0.25)]">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-darkish-pink/10">
            <svg
              className="h-6 w-6 text-darkish-pink"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>
          <p className="text-lg font-semibold text-charcoal-grey">
            {access.userMessage ?? 'Ocurrió un error cargando la cuenta.'}
          </p>
          {access.technicalMessage && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-brownish-grey hover:text-charcoal-grey">
                Detalles técnicos
              </summary>
              <p className="mt-1 rounded bg-pale-grey px-3 py-2 text-left text-xs text-brownish-grey">
                {access.technicalMessage}
              </p>
            </details>
          )}
          <div className="mt-5">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary/90"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
